import { User as UserJSON } from 'domain/User'
import { getRoot, IStateTreeNode, resolveIdentifier, types } from 'mobx-state-tree'

export interface IFreightieJSON {
  _id: string
  email?: string
  name?: string
  thumbnailUrl?: string
}

export type ActorsJSON = {
  users: UserJSON[]
  freighties: IFreightieJSON[]
}

export const getInitials = (user: UserJSON) => `${user.firstName.slice(0, 1)}${user.lastName.slice(0, 1)}`.toUpperCase()

export const User = types
  .model('User', {
    _id: types.identifier(types.string),
    customer_id: types.string,
    firstName: types.string,
    lastName: types.string,
    email: types.string,
  })
  .views((self) => ({
    get name() {
      return `${self.firstName} ${self.lastName}`
    },
    get initials() {
      return getInitials(self as UserJSON)
    },
  }))

export const Users = types.model('Users', {
  users: types.array(User),
})
export type TUsers = typeof Users.Type

export const Freightie = types
  .model('Freightie', {
    _id: types.identifier(types.string),
    email: types.optional(types.string, ''),
    name: types.optional(types.string, ''),
    thumbnailUrl: types.optional(types.string, ''),
  })
  .views((self) => ({
    get initials(): string {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions -- TODO: Fix template expression
      return self.name.replace(/^(\w).*?\s(\w).*$/, (match, p1, p2) => `${p1.toUpperCase()}${p2.toUpperCase()}`)
    },
  }))

export const Actors = types
  .model('Actors', {
    users: types.array(User),
    freighties: types.array(Freightie),
  })
  .actions((self) => ({
    addFreightie(freightie: IFreightieJSON) {
      if (!resolveIdentifier(Freightie, self.freighties as IStateTreeNode, freightie._id)) {
        self.freighties.push(freightie as TFreightie)
      }
    },
    addUser(user: Partial<UserJSON>) {
      // @ts-expect-error TS(2345) FIXME: Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
      if (!resolveIdentifier(User, self.users as IStateTreeNode, user._id)) {
        self.users.push(user as TUser)
      }
    },
    replaceUsers(users: TUser[]): void {
      self.users.replace(users)
    },
  }))
  .actions((self) => ({
    addActors(newActors: ActorsJSON) {
      newActors.freighties.forEach((freightie) => self.addFreightie(freightie))
      newActors.users.forEach((user) => self.addUser(user))
    },
  }))

const FreightieOrUserActor = types.union(Freightie, User)

export type TUser = typeof User.Type
export type TFreightie = typeof Freightie.Type
export type TActor = TUser | TFreightie

export const ReferenceUser = types.maybe(
  types.reference(User, {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
    get(identifier: string, parent: any) {
      const activityFeed = getRoot(parent)
      return activityFeed.actors.users.find((user) => user._id === identifier) || null
    },
    set(user: TUser) {
      return user._id
    },
  })
)

export const ReferenceFreightieOrUser = types.maybe(
  types.reference(FreightieOrUserActor, {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
    get(identifier: string, parent: any) {
      const activityFeed = getRoot(parent)
      const { freighties, users } = activityFeed.actors
      return (
        freighties.find((freightie) => freightie._id === identifier) ||
        users.find((user) => user._id === identifier) ||
        null
      )
    },
    set(freightieOrUser: TFreightie | TUser) {
      return freightieOrUser._id
    },
  })
)
